import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Link } from 'react-router-dom';
import PostPurchaseModal from '../components/PostPurchase/PostPurchaseModal';
import PriceChartingWidget from '../components/PriceChartingWidget/PriceChartingWidget';
import './ListingDetail.css';
import { usePostHog } from '@posthog/react';

const loadPayPalScript = () => {
    return new Promise((resolve) => {
        const script = document.createElement('script');
        const clientid = process.env.REACT_APP_PAYPAL_CLIENT_ID;
        script.src = `https://www.paypal.com/sdk/js?client-id=${clientid}&currency=USD&components=buttons&enable-funding=card&disable-funding=paylater&intent=authorize`;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

const ListingDetail = () => {
    const { certNumber } = useParams();
    const navigate = useNavigate();
    const posthog = usePostHog();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [flavorTextDisplay, setFlavorTextDisplay] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [psaTip, setPsaTip] = useState(null); // pointer position for the "View on PSA" tooltip
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const [orderNumber, setOrderNumber] = useState(null);
    const [orderAccessKey, setOrderAccessKey] = useState(null);

    const createOrderOnBackend = async (paypalOrderID) => {
        const orderPayload = {
          price: listing.price,
          cert_number: certNumber,
          paypal_order_id: paypalOrderID,
          // Include any additional order details you have available
          // If you have buyer information collected separately, include it here
        };
      
        try {
          const response = await fetch(`${apiBaseUrl}/orders`, {
            method: 'POST',
            credentials: 'include', // Ensures cookies are sent with cross-origin requests
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(orderPayload),
          });
      
          if (response.ok) {
            const data = await response.json();
            setOrderNumber(data.order_number);
            setOrderAccessKey(data.access_key);
            setShowSuccessModal(true);
            posthog.capture('order_created', {
                cert_number: certNumber,
                order_number: data.order_number,
                price: listing.price,
                paypal_order_id: paypalOrderID,
            });
          } else {
            const errorData = await response.json();
            console.error('Order creation failed:', errorData);
            posthog.captureException(new Error('Order creation failed'), { cert_number: certNumber });
          }
        } catch (error) {
          console.error('Order creation error:', error);
          posthog.captureException(error, { cert_number: certNumber });
        }
      };

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchListing = async () => {
            try {
                const response = await fetch(`${apiBaseUrl}/listings?certNumber=${certNumber}`, {
                    method: 'GET',
                    credentials: 'include', // Ensures cookies are sent with cross-origin requests
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error(`Network response was not ok: ${response.statusText}`);
                }

                const result = await response.json();
                if (result.status === 'success' && result.listings.length > 0) {
                    const fetchedListing = result.listings[0];
                    setListing(fetchedListing);
                    posthog.capture('listing_viewed', {
                        cert_number: certNumber,
                        card_name: fetchedListing.cert?.card?.name,
                        grade: fetchedListing.cert?.grade,
                        price: fetchedListing.price,
                        set: fetchedListing.cert?.card?.set?.name,
                    });
                } else {
                    throw new Error('Listing not found');
                }
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchListing();
    }, [certNumber]);

    useEffect(() => {
        if (listing && listing.cert.card.flavor_text) {
            let i = 0;
            const text = listing.cert.card.flavor_text;
            const interval = setInterval(() => {
                setFlavorTextDisplay((prev) => prev + text[i]);
                i++;
                if (i === text.length) clearInterval(interval);
            }, 30);
        }
    }, [listing]);

    useEffect(() => {
        const renderPayPalButtons = async () => {
          const loaded = await loadPayPalScript();
          if (loaded && window.paypal) {
            window.paypal.Buttons({
              createOrder: (data, actions) => {
                // Create the order on PayPal but do not capture it
                return actions.order.create({
                  purchase_units: [{
                    amount: {
                      value: listing.price.toFixed(2),
                    },
                  }],
                });
              },
              onApprove: (data, actions) => {
                // Payment approved by buyer, send orderID to backend
                createOrderOnBackend(data.orderID);
              },
              onError: (err) => {
                console.error('PayPal Checkout Error:', err);
              },
              style: {
                color: 'blue' // Options: 'gold', 'blue', 'silver', 'white', 'black'
              }
            }).render('#paypal-button-container');
            posthog.capture('checkout_initiated', {
                cert_number: certNumber,
                price: listing.price,
                card_name: listing.cert?.card?.name,
                grade: listing.cert?.grade,
            });
          } else {
            console.error('PayPal SDK failed to load.');
          }
        };

        if (listing) {
          renderPayPalButtons();
        }
      }, [listing]);
      
      
      if (loading) return <p></p>;

    if (error) return <p>Error loading listing: {error}</p>;
    if (!listing) return <p></p>;

    const { cert, price, cert: { card } } = listing;
    const frontImageUrl = cert.cert_images.front_image_url || 'https://via.placeholder.com/200';
    const grade = cert.grade || 'N/A';
    const name = card?.name || cert.name || 'Unknown';
    const certSet = cert.set || 'Unknown';
    const cardSet = card?.set || 'Unknown';
    const flavorText = card?.flavor_text || '';
    const illustrator = card?.artist || 'Unknown';

    const togglePane = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <Layout>
            {loading && (
                <div className="fast-loading-indicator">
                    <span className="dot one">.</span>
                    <span className="dot two">.</span>
                    <span className="dot three">.</span>
                </div>
            )}
            <div className="listing-detail">
                <div
                    className={`listing-info glass-pane ${isExpanded ? 'expanded' : 'collapsed'}`}
                    onClick={togglePane}
                >
                    <div className="name-grade">
                        <div className="name-container">
                        <h1><i>{name}</i></h1>
                            {cert.promo?.display_name && (
                                <p className="promo-name"><i>{cert.promo.display_name}</i></p>
                            )}
                        </div>
                        <span className="grade">{grade}</span>
                    </div>
                    <div className="details-container">
                        <div className="card-details">
                            <p><i>{cardSet.series === cardSet.name ? cardSet.name : cardSet.series + ": " + cardSet.name}</i></p>
                            <p className="illustrator">Illustrated by <Link to={`/artists/${illustrator}`}>{illustrator}</Link></p>
                        </div>
                        <div className="pricecharting-container">
                        <PriceChartingWidget
                            cert={cert}
                            onOpen={() => posthog.capture('pricecharting_clicked', {
                                cert_number: certNumber,
                                price_charting_id: cert.price_charting_id,
                                card_name: name,
                                grade,
                            })}
                        />
                                
                            </div>
                            
                        </div>
                        {flavorText && (
                            <div className="flavor-text-box">
                                <p className="flavor-text"><i>{flavorTextDisplay}</i></p>
                            </div>
                        )}
                        <div className="checkout">
                            <div className="price-details">
                                <p>Price <span>${price.toFixed(2)}</span></p>
                                <p>Shipping <span>$0.00</span></p>
                                <hr />
                                <p className="total">Total <span>${(price).toFixed(2)}</span></p>
                            </div>
                            <div id="paypal-button-container"></div>
                        </div>
                    </div>
                <div className="listing-image">
                    {/* Links to the cert on PSA. On hover the cert image lifts and a
                        "View on PSA" tooltip follows the pointer. */}
                    <a
                        href={`https://www.psacard.com/cert/${certNumber}/psa`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="listing-image-link"
                        aria-label={`View cert ${certNumber} on PSA (opens in a new tab)`}
                        onMouseMove={(e) => setPsaTip({ x: e.clientX, y: e.clientY })}
                        onMouseLeave={() => setPsaTip(null)}
                        onClick={() => posthog.capture('psa_cert_clicked', { cert_number: certNumber, card_name: name, grade })}
                    >
                        <img src={frontImageUrl} alt={name} />
                    </a>
                    {/* Portaled to <body>: .listing-image is transformed, which
                        would make position: fixed relative to it, not the viewport. */}
                    {psaTip && createPortal(
                        <div className="listing-psa-tip" style={{ left: psaTip.x, top: psaTip.y }} aria-hidden="true">
                            View on PSA
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 4h6v6" />
                                <line x1="20" y1="4" x2="11" y2="13" />
                                <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
                            </svg>
                        </div>,
                        document.body
                    )}
                </div>
            </div>
            {showSuccessModal && (
                <PostPurchaseModal onConfirm={() => navigate(`/orders/${orderNumber}?key=${orderAccessKey || ''}`)} />
            )}
        </Layout>
    );
};

export default ListingDetail;
